import { Dropdown, IDropdownOption, ITextFieldProps, Label, Stack, TextField, Toggle } from '@fluentui/react';
import { IStyleSetBase } from '@fluentui/style-utilities';
import { BasicList, basicListStylesInput, IItemProps, Item, itemStylesDropdown, TextListEditor } from '@h2oai/ui-kit';
import { useState } from 'react';

import { EntityType } from '../../../aiem/entity/constants';
import {
  DurationEntityField,
  EntityField,
  NumberEntityField,
  SelectEnumEntityField,
  SemverEntityField,
} from '../../../aiem/entity/types';
import { bytesToGibibytes, gibibytesToBigIntStringBytes } from '../../../aiem/utils';
import { usePermissions } from '../../../authz/providers/PermissionsProvider/PermissionsProvider';
import { LabelIconTooltip } from '../../../components/AIEnginesPage/components/LabelIconTooltip/LabelIconTooltip';
import { ICodeAreaStyles } from '../../../components/CodeArea/CodeArea.styles';
import SpinnerWithTooltip from '../../../components/SpinnerWithTooltip/SpinnerWithTooltip';
import { useFormAttributes } from '../../../utils/utils';
import { defaultEntityFormRowStyles } from './DefaultEntityFormRowStyles';
import { LabelAndDescription } from './LabelAndDescription';

export interface EntityFieldInputProps<EntityModel, Field extends EntityField<EntityModel> = EntityField<EntityModel>> {
  key?: string;
  field: Field;
  model: EntityModel;
  onChange?: (name: keyof EntityModel, value: any) => void;
  onChangeMultiple?: (partialModel: Partial<EntityModel>) => void;
  entityType: EntityType;
  isCreate?: boolean;
  validate?: (valid: boolean) => void;
  disabled?: boolean;
  largeLabel?: boolean;
  compact?: boolean;
  textAreaStyles?: Partial<ICodeAreaStyles>;
  required?: boolean;
  rootStyles?: IStyleSetBase;
  editorWidth?: number | string;
}

export interface NumberEntityFieldInputProps<EntityModel>
  extends EntityFieldInputProps<EntityModel, NumberEntityField<EntityModel>> {
  convertToGibibytes?: boolean;
  suffix?: string;
}

export type DurationEntityFieldInputProps<EntityModel> = EntityFieldInputProps<
  EntityModel,
  DurationEntityField<EntityModel>
>;

export type SemverEntityFieldInputProps<EntityModel> = EntityFieldInputProps<
  EntityModel,
  SemverEntityField<EntityModel>
>;

export type SelectEnumEntityFieldInputProps<EntityModel> = EntityFieldInputProps<
  EntityModel,
  SelectEnumEntityField<EntityModel>
>;

type FormRowProps = { singleRow?: boolean; children: React.ReactNode };

export const FormRow = ({ singleRow = false, children }: FormRowProps) => {
  const { inputContainerProps, inputRowProps, singleRowInputRowProps } = useFormAttributes();
  return (
    <Stack {...(singleRow ? singleRowInputRowProps : inputRowProps)} style={defaultEntityFormRowStyles}>
      <Stack {...inputContainerProps}>{children}</Stack>
    </Stack>
  );
};

export function TextEntityModelField<EntityModel>({
  field,
  model,
  onChange,
  disabled = false,
  largeLabel = false,
}: EntityFieldInputProps<EntityModel>) {
  const { name, label, description, required, inputDefaultValue } = field;
  const [fieldValue, setFieldValue] = useState<string>((model as any)[name] as string);
  const fieldComponent = (
    <TextField
      styles={{ root: { width: 300 } }}
      required={required}
      disabled={disabled}
      autoFocus
      label={largeLabel ? undefined : label}
      data-test={`admin-setting-${String(name)}-input`}
      value={fieldValue ?? (inputDefaultValue || '')}
      onChange={(_ev, value) => {
        setFieldValue(value || '');
        onChange && onChange(name, value);
      }}
      onRenderLabel={
        largeLabel
          ? undefined
          : (
              labelProps: ITextFieldProps | undefined,
              defaultRender: ((props?: ITextFieldProps | undefined) => JSX.Element | null) | undefined
            ) => (
              <LabelIconTooltip
                id={labelProps?.id}
                data-test={`admin-settings-${String(name)}-info`}
                label={defaultRender!(labelProps) as any}
                tooltip={description}
              />
            )
      }
    />
  );

  return largeLabel ? (
    <Stack horizontal tokens={{ childrenGap: 15 }} style={defaultEntityFormRowStyles}>
      {largeLabel && <LabelAndDescription label={label} required={field.required} description={description} />}
      {fieldComponent}
    </Stack>
  ) : (
    <FormRow>{fieldComponent}</FormRow>
  );
}

export function NumberEntityModelField<EntityModel>({
  field,
  model,
  onChange,
  convertToGibibytes = false,
  disabled = false,
  largeLabel = false,
}: NumberEntityFieldInputProps<EntityModel>) {
  const {
    name,
    label,
    description,
    minimum,
    maximum,
    disabled: fieldDisabled = disabled,
    fieldUnsetText = 'No limit',
    inputDefaultValue,
  } = field;
  const handleChange = (value: number | undefined) => onChange!(name, value);
  const fieldValue = model[name];
  const shouldConvert = convertToGibibytes || field.convertToGibibytes;

  const min = field.minimumField ? model[field.minimumField] : minimum;
  const max = field.maximumField ? model[field.maximumField] : maximum;

  const convertValue = (value: any) => {
    if (!value) return undefined;
    const numValue = Number(shouldConvert ? bytesToGibibytes(value) : value);
    return isNaN(numValue) ? undefined : numValue;
  };

  const fieldComponent = (
    <Stack horizontal verticalAlign="center" horizontalAlign="center">
      {field.togglable && (
        <Stack verticalAlign="center" style={{ height: 30, marginRight: 15 }}>
          <Toggle
            disabled={disabled}
            checked={fieldValue !== undefined}
            onChange={
              disabled
                ? () => null
                : (_, checked) => {
                    handleChange(checked ? Number(fieldValue || inputDefaultValue || min) : undefined);
                  }
            }
          />
        </Stack>
      )}

      <Stack verticalAlign="center" style={{ height: 30 }}>
        {field.togglable && fieldValue === undefined ? (
          <p>{fieldUnsetText}</p>
        ) : (
          <SpinnerWithTooltip
            onChange={(_ev: unknown, value: unknown) => {
              const converted = shouldConvert ? gibibytesToBigIntStringBytes(Number(value)) : Number(value);
              onChange && onChange(name, converted);
            }}
            value={convertValue(fieldValue) ?? 0}
            min={convertValue(min)}
            max={convertValue(max)}
            suffix={shouldConvert ? 'GiB' : ''}
            disabled={fieldDisabled}
          />
        )}
      </Stack>
    </Stack>
  );

  return largeLabel ? (
    <Stack horizontal tokens={{ childrenGap: 15 }} style={defaultEntityFormRowStyles}>
      <LabelAndDescription label={label} description={description} />
      {fieldComponent}
    </Stack>
  ) : (
    <FormRow>{fieldComponent}</FormRow>
  );
}

export function BooleanEntityModelField<EntityModel>({
  field,
  model,
  onChange,
  disabled = false,
  largeLabel = false,
  required = false,
}: EntityFieldInputProps<EntityModel>) {
  const { name, label, description } = field;
  const checked: boolean = Boolean((model as any)[name]) as boolean;
  return (
    <Stack horizontal tokens={{ childrenGap: 15 }} style={defaultEntityFormRowStyles}>
      {largeLabel && <LabelAndDescription label={label} required={required} description={description} />}
      <Toggle
        checked={checked}
        onChange={(_ev, newValue) => (onChange ? onChange(name, newValue) : undefined)}
        inlineLabel
        label={largeLabel ? undefined : label}
        disabled={disabled}
      />
    </Stack>
  );
}

export function SelectEnumEntityModelField<EntityModel>({
  field,
  model,
  onChange,
  largeLabel = false,
}: SelectEnumEntityFieldInputProps<EntityModel>) {
  const { name, label, options, selectedOption, description, disabled } = field;
  const [fieldValue, setFieldValue] = useState<string>(((model as any)[name] as string) || selectedOption || '');
  const fieldComponent = options ? (
    <Dropdown
      disabled={disabled}
      styles={{ root: { width: 215 } }}
      options={options}
      selectedKey={fieldValue}
      onChange={(_ev, v) => {
        const value = v?.key as string;
        setFieldValue(value);
        onChange && onChange(name, value);
      }}
    />
  ) : (
    <>This field has an implementation error</>
  );

  return largeLabel ? (
    <Stack horizontal tokens={{ childrenGap: 15 }} style={defaultEntityFormRowStyles}>
      {largeLabel && <LabelAndDescription label={label} required={field.required} description={description} />}
      {fieldComponent}
    </Stack>
  ) : (
    <FormRow>
      <Label required={field.required}>{label}</Label>
      {fieldComponent}
    </FormRow>
  );
}

export function StringArrayEntityModelField<EntityModel>({
  field,
  model,
  onChange,
  largeLabel = false,
}: EntityFieldInputProps<EntityModel>) {
  const { name, label, description, disabled } = field;
  const items: string[] = [...(model as any)[name]] as string[];

  const fieldComponent = (
    <TextListEditor
      items={items}
      textFieldProps={{ disabled }}
      itemProps={{ hasClose: !disabled } as IItemProps<any>}
      onChange={disabled ? () => null : (value: string[]) => (onChange ? onChange(name, value) : undefined)}
    />
  );

  return largeLabel ? (
    <Stack horizontal tokens={{ childrenGap: 15 }} style={defaultEntityFormRowStyles}>
      {largeLabel && <LabelAndDescription label={label} required={field.required} description={description} />}
      {fieldComponent}
    </Stack>
  ) : (
    <FormRow>
      <Label>{label}</Label>
      {fieldComponent}
    </FormRow>
  );
}

export function ReadOnlyStringArrayEntityModelField<EntityModel>({ field, model }: EntityFieldInputProps<EntityModel>) {
  const { name, label } = field;
  const items: string[] = (model as any)[name] as string[];
  return (
    <FormRow>
      <Label>{label}</Label>
      <BasicList
        title={label}
        styles={basicListStylesInput}
        idField="key"
        labelField="text"
        data={[{ key: '__input__', text: 'input' }, ...items?.map((d) => ({ key: d, text: d }))]}
        data-test="text-list-editor"
        horizontal
        itemRenderer={(d: IDropdownOption) => (
          <Item
            key={`${d.key}-string-array-item`}
            styles={itemStylesDropdown}
            data={d}
            idField="key"
            labelField="text"
          />
        )}
      />
    </FormRow>
  );
}

export function LatestAndAliasesEntityModelField<EntityModel>({
  field,
  model,
  onChange,
  disabled = false,
}: EntityFieldInputProps<EntityModel>) {
  const { name, label, permitActions } = field;
  const permissionsArray = usePermissions(permitActions || []);
  const permissions = permissionsArray.slice(0, -1); // All permissions except loading
  const canDoActions = permitActions?.length ? permissions.every(Boolean) : true;
  const originalItems = (model as any)[name] as string[];
  const [items, setItems] = useState<string[]>(originalItems);
  const [latest, setLatest] = useState<boolean>(originalItems.includes('latest'));
  const fieldDisabled = !canDoActions || disabled;

  return (
    <>
      <FormRow singleRow>
        <Toggle
          checked={latest}
          onChange={(_ev, checked) => {
            const newItems = [
              ...(checked ? ['latest'] : []),
              ...(checked ? items : items.filter((item) => item !== 'latest')),
            ];
            setItems(newItems);
            setLatest(Boolean(checked));
            onChange && onChange(name, newItems);
          }}
          inlineLabel
          label={'Latest'}
          disabled={fieldDisabled}
        />
      </FormRow>
      <FormRow>
        <Label>{label}</Label>
        <TextListEditor
          items={items}
          textFieldProps={{ disabled: fieldDisabled }}
          itemProps={{ hasClose: !fieldDisabled } as IItemProps<any>}
          onChange={
            fieldDisabled
              ? () => null
              : (value: string[]) => {
                  setItems(value);
                  setLatest(value.includes('latest'));
                  onChange && onChange(name, value);
                }
          }
        />
      </FormRow>
    </>
  );
}
